import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Badge,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
} from '@command-center/ui-system';
import { voiceManagementApi } from '../lib/ai-systems-api';

export function VoiceAgentManagementDialog({ open, onOpenChange }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'import' | 'create'>('import');
  const [providerAccountId, setProviderAccountId] = useState('');
  const [assistantId, setAssistantId] = useState('');
  const [templateVersionId, setTemplateVersionId] = useState('');
  const [name, setName] = useState('');
  const accounts = useQuery({
    queryKey: ['voice-management', 'accounts'],
    queryFn: voiceManagementApi.providerAccounts,
    enabled: open,
    retry: false,
  });
  const templates = useQuery({
    queryKey: ['voice-management', 'templates'],
    queryFn: voiceManagementApi.templates,
    enabled: open && mode === 'create',
    retry: false,
  });
  const assistants = useQuery({
    queryKey: ['voice-management', 'assistants', providerAccountId],
    queryFn: () => voiceManagementApi.providerAssistants(providerAccountId),
    enabled: open && mode === 'import' && Boolean(providerAccountId),
    retry: false,
  });

  useEffect(() => {
    if (!providerAccountId && accounts.data?.[0]) setProviderAccountId(accounts.data[0].id);
  }, [accounts.data, providerAccountId]);
  useEffect(() => {
    const version = templates.data?.[0]?.currentVersion?.id;
    if (!templateVersionId && version) setTemplateVersionId(version);
  }, [templateVersionId, templates.data]);

  const save = useMutation({
    mutationFn: () => mode === 'import'
      ? voiceManagementApi.importAgent({
          providerAccountId,
          providerAssistantId: assistantId,
          displayName: name || undefined,
        })
      : voiceManagementApi.createAgent({ providerAccountId, templateVersionId, name }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['ai-systems', 'agents'] });
      onOpenChange(false);
    },
  });
  const error = save.error ?? accounts.error ?? templates.error ?? assistants.error;
  const valid = Boolean(providerAccountId && (mode === 'import' ? assistantId : templateVersionId && name));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add Cascade Voice Agent</DialogTitle>
          <DialogDescription>
            Import an allocated Vapi assistant without changing it, or create a receptionist draft. Tenant scope is always Cascade.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="inline-flex rounded-md border border-border p-0.5">
            <Button size="sm" variant={mode === 'import' ? 'default' : 'ghost'} onClick={() => setMode('import')}>Import Vapi Agent</Button>
            <Button size="sm" variant={mode === 'create' ? 'default' : 'ghost'} onClick={() => setMode('create')}>Create Receptionist</Button>
          </div>
          <Field label="Provider account">
            <select className="h-10 w-full rounded-md border border-input bg-background px-3" value={providerAccountId} onChange={(event) => setProviderAccountId(event.target.value)}>
              <option value="">Select provider account</option>
              {(accounts.data ?? []).map((account) => <option key={account.id} value={account.id}>{account.provider} · {account.account_type}</option>)}
            </select>
          </Field>
          {mode === 'import' ? (
            <Field label="Vapi Assistant ID">
              <Input value={assistantId} onChange={(event) => setAssistantId(event.target.value)} list="cascade-vapi-assistants" placeholder="Paste or select an Assistant ID" />
              <datalist id="cascade-vapi-assistants">
                {(assistants.data ?? []).map((assistant) => <option key={assistant.providerResourceId} value={assistant.providerResourceId}>{assistant.name ?? assistant.providerResourceId}</option>)}
              </datalist>
            </Field>
          ) : (
            <Field label="Starter template">
              <select className="h-10 w-full rounded-md border border-input bg-background px-3" value={templateVersionId} onChange={(event) => setTemplateVersionId(event.target.value)}>
                <option value="">Select template</option>
                {(templates.data ?? []).map((template) => template.currentVersion && <option key={template.currentVersion.id} value={template.currentVersion.id}>{template.name}</option>)}
              </select>
            </Field>
          )}
          <Field label={mode === 'import' ? 'Display name (optional)' : 'Agent name'}>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
          {error && <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error instanceof Error ? error.message : 'Voice management is unavailable.'}</p>}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : mode === 'import' ? 'Import Agent' : 'Create Draft'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ManagedVoiceAgentsPanel() {
  const queryClient = useQueryClient();
  const agents = useQuery({
    queryKey: ['voice-management', 'managed-agents'],
    queryFn: voiceManagementApi.managedAgents,
    retry: false,
  });
  const publish = useMutation({
    mutationFn: (agent: NonNullable<typeof agents.data>[number]) => voiceManagementApi.publishAgent(
      agent.id,
      agent.current_draft_version_id ?? '',
      agent.currentDraft?.provider_account_id ?? '',
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['voice-management', 'managed-agents'] });
      await queryClient.invalidateQueries({ queryKey: ['ai-systems', 'agents'] });
    },
  });
  if (agents.isError || !agents.data?.length) return null;
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <p className="text-sm font-semibold">Supabase-managed configurations</p>
        <p className="text-xs text-muted-foreground">Cascade-only immutable drafts and published versions.</p>
      </div>
      <div className="divide-y divide-border">
        {agents.data.map((agent) => {
          const hasDraft = Boolean(agent.current_draft_version_id && agent.current_draft_version_id !== agent.published_version_id);
          return (
            <div key={agent.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div><span className="text-sm font-medium">{agent.name}</span> <Badge variant="secondary">{agent.lifecycle_status}</Badge><p className="text-xs text-muted-foreground">{agent.operational_role}</p></div>
              {hasDraft && <Button size="sm" disabled={publish.isPending || !agent.currentDraft?.provider_account_id} onClick={() => publish.mutate(agent)}>Publish draft v{agent.currentDraft?.version_number}</Button>}
            </div>
          );
        })}
      </div>
      {publish.error && <p className="border-t border-destructive/30 px-4 py-2 text-xs text-destructive">{publish.error.message}</p>}
    </Card>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block space-y-1.5 text-sm font-medium"><span>{label}</span>{children}</label>;
}
